import { ModuleLayer } from "Modeling/Components/ModuleLayerToggle";
import {
	createContext,
	type ReactNode,
	useContext,
	useMemo,
	useState,
} from "react";

/**
 * The layer actually rendered by <ModuleShell />. Extends the two user-facing
 * toggle layers (advisor/client) with a print-only `Pdf` layer used when a
 * module is rendered into a client PDF. `Pdf` is never selectable in the toggle
 * — it is only pinned via `lockedLayer={ModuleRenderLayer.Pdf}` (the PDF print
 * page). Modules that don't supply a dedicated PDF layer fall back to the client
 * layer, so today PDF === client view at zero per-module cost.
 */
export const ModuleRenderLayer = {
	...ModuleLayer,
	Pdf: "pdf",
} as const;
export type ModuleRenderLayer =
	(typeof ModuleRenderLayer)[keyof typeof ModuleRenderLayer];

/** Whether the module is rendered for the screen or for print (PDF). */
export const ModulePresentation = {
	Screen: "screen",
	Print: "print",
} as const;
export type ModulePresentation =
	(typeof ModulePresentation)[keyof typeof ModulePresentation];

type ModuleLayerContextValue = {
	/** The layer being rendered — advisor / client / pdf. */
	layer: ModuleRenderLayer;
	/** The toggle-selectable layer (advisor/client); equals `layer` when not pinned. */
	toggleLayer: ModuleLayer;
	setLayer: (next: ModuleLayer) => void;
	/** When true the layer is fixed and the Poradenská/Klientská toggle is hidden. */
	locked: boolean;
	/**
	 * "print" when the module is rendered into a PDF (`lockedLayer` Pdf),
	 * otherwise "screen". Shared components (e.g. charts) can read this to adapt
	 * for print — e.g. static data labels instead of hover-only tooltips — without
	 * needing a separate per-module view.
	 */
	presentation: ModulePresentation;
};

const ModuleLayerContext = createContext<ModuleLayerContextValue | undefined>(
	undefined,
);

/**
 * Lifts the advisor/client layer toggle state out of individual <ModuleShell />
 * instances so the choice survives module-to-module navigation within the same
 * session (per user request: clicking "Pokračovat" or otherwise switching
 * modules keeps Klientská active). Resets on hard reload — intentional.
 *
 * Pass {@link lockedLayer} to pin the layer (e.g. the public client-share view
 * locks it to "client"): the toggle is hidden and `setLayer` becomes a no-op.
 */
export const ModuleLayerProvider = ({
	children,
	lockedLayer,
}: {
	children: ReactNode;
	lockedLayer?: ModuleRenderLayer;
}) => {
	// The user's toggle selection — always one of the two interactive layers.
	// The render layer is either this (unlocked) or the pinned `lockedLayer`.
	const [selected, setSelected] = useState<ModuleLayer>(
		lockedLayer === ModuleLayer.Client
			? ModuleLayer.Client
			: ModuleLayer.Advisor,
	);
	const value = useMemo<ModuleLayerContextValue>(
		() =>
			lockedLayer
				? {
						layer: lockedLayer,
						toggleLayer: selected, // unused while locked (toggle hidden)
						setLayer: () => undefined,
						locked: true,
						presentation:
							lockedLayer === ModuleRenderLayer.Pdf
								? ModulePresentation.Print
								: ModulePresentation.Screen,
					}
				: {
						layer: selected,
						toggleLayer: selected,
						setLayer: setSelected,
						locked: false,
						presentation: ModulePresentation.Screen,
					},
		[selected, lockedLayer],
	);
	return (
		<ModuleLayerContext.Provider value={value}>
			{children}
		</ModuleLayerContext.Provider>
	);
};

export const useModuleLayer = (): ModuleLayerContextValue => {
	const ctx = useContext(ModuleLayerContext);
	if (!ctx) {
		throw new Error(
			"useModuleLayer must be used within a <ModuleLayerProvider>",
		);
	}
	return ctx;
};
